import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { OBTAIN_LMS_TOKEN } from "../../Queries";
import { Course } from "../../types";

export interface GradePushModalProps {
  course: Course;
  onClose: () => void;
  onConnected: () => void;
  onSkip: () => void;
  context?: "push" | "send" | "schedule";
}

export const GradePushModal: React.FC<GradePushModalProps> = ({
  course,
  onClose,
  onConnected,
  onSkip,
  context = "push",
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [obtainLmsToken] = useMutation(OBTAIN_LMS_TOKEN);
  const handledRef = useRef(false);

  const handleOAuthSuccess = useCallback(
    async (code: string, state: string) => {
      setLoading(true);
      setError(null);
      try {
        await obtainLmsToken({
          variables: { code, state },
          context: { debounceKey: "obtainLmsToken" },
        });
        onConnected();
        onClose();
      } catch (e) {
        setError("Failed to connect to LMS. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [obtainLmsToken, onConnected, onClose],
  );

  const handleOAuthMessage = useCallback(
    (event: MessageEvent<any>) => {
      if (handledRef.current) return;
      const data = event.data as any;
      if (!data || typeof data !== "object") return;
      // Optional: restrict to same-origin messages
      if (event.origin && event.origin !== window.location.origin) return;
      if (data.type === "OAUTH_SUCCESS") {
        handledRef.current = true;
        window.removeEventListener("message", handleOAuthMessage as any);
        if (data.platform === "moodle") {
          onConnected();
          onClose();
          return;
        }
        if (data.code && data.state) {
          handleOAuthSuccess(data.code, data.state);
        }
      } else if (data.type === "OAUTH_ERROR") {
        handledRef.current = true;
        window.removeEventListener("message", handleOAuthMessage as any);
        setError(data.error || "OAuth connection failed");
      }
    },
    [handleOAuthSuccess, onConnected, onClose],
  );

  useEffect(() => {
    window.addEventListener("message", handleOAuthMessage as any);
    return () => {
      window.removeEventListener("message", handleOAuthMessage as any);
    };
  }, [handleOAuthMessage]);

  function openOAuth() {
    setError(null);
    if (!course?.oauthUrl) return;
    window.open(
      course.oauthUrl,
      `${course.lmsType || "LMS"} Connect`,
      `dependent=${1}, alwaysOnTop=${1}, width=${800}, height=${450}`,
    );
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>
        {context === "push"
          ? "Enable enhanced grade sync?"
          : context === "send"
            ? "Connect your LMS for enhanced grade sync before sending?"
            : "Connect your LMS for enhanced grade sync before scheduling?"}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText>
          {context === "push" && (
            <>
              Use your LMS API to push all grades in bulk for faster, more
              reliable sync. You can authorize now, or continue with the
              standard LTI-based push.
            </>
          )}
          {context === "send" && (
            <>
              Connect now to automatically track assignment submissions and
              lateness as students submit, making final grade sync seamless.
            </>
          )}
          {context === "schedule" && (
            <>
              Before scheduling, connect your LMS so we can track submissions
              and late penalties automatically once students start submitting.
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            onSkip();
          }}
        >
          Skip for now
        </Button>
        <Button onClick={openOAuth} disabled={loading} autoFocus>
          {loading ? "Connecting..." : `Login with ${course.lmsType || "LMS"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
